import type { ProjectMilestone, ProjectMilestoneStatus } from './project.js';

/** Calendar day as YYYY-MM-DD in UTC derived from a Date (avoids local midnight skew for comparisons). */
export function toYyyyMmDdUtc(d: Date): string {
  const y = d.getUTCFullYear();
  const m = String(d.getUTCMonth() + 1).padStart(2, '0');
  const day = String(d.getUTCDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function parseYyyyMmDdUtc(isoDate: string): number {
  const parts = isoDate.trim().split('-').map((p) => Number(p));
  if (parts.length !== 3 || parts.some((n) => !Number.isFinite(n))) {
    return NaN;
  }
  const [y, mo, da] = parts;
  return Date.UTC(y, mo - 1, da);
}

/**
 * Share of milestones in status Completed, 0–100 integer. Empty list → 0.
 */
export function computeProjectProgressPercent(
  milestones: Pick<ProjectMilestone, 'status'>[],
): number {
  if (!milestones.length) {
    return 0;
  }
  const done = milestones.filter((m) => m.status === 'Completed').length;
  return Math.round((100 * done) / milestones.length);
}

function milestoneOrder(m: Pick<ProjectMilestone, 'order'>): number {
  return m.order ?? 0;
}

function compareUpcoming(
  a: Pick<ProjectMilestone, 'order' | 'dueDate'>,
  b: Pick<ProjectMilestone, 'order' | 'dueDate'>,
): number {
  const oa = milestoneOrder(a);
  const ob = milestoneOrder(b);
  if (oa !== ob) {
    return oa - ob;
  }
  const da = a.dueDate ? parseYyyyMmDdUtc(a.dueDate) : Number.POSITIVE_INFINITY;
  const db = b.dueDate ? parseYyyyMmDdUtc(b.dueDate) : Number.POSITIVE_INFINITY;
  if (da !== db) {
    return da - db;
  }
  return 0;
}

/** Not-yet-completed milestones, ordered by `order` then `dueDate`, limited. */
export function getUpcomingMilestones<T extends ProjectMilestone>(
  milestones: T[],
  limit = 3,
): T[] {
  const open = milestones.filter((m) => m.status !== 'Completed');
  open.sort(compareUpcoming);
  return open.slice(0, limit);
}

const OVERDUE_STATUSES: ProjectMilestoneStatus[] = [
  'NotStarted',
  'InProgress',
  'Handover',
  'Blocked',
];

/**
 * True when milestone has a due date, is not completed, and due date is strictly before `today` (UTC calendar day).
 */
export function isMilestoneOverdue(
  milestone: Pick<ProjectMilestone, 'status' | 'dueDate'>,
  today: Date,
): boolean {
  if (!milestone.dueDate?.trim()) {
    return false;
  }
  if (!OVERDUE_STATUSES.includes(milestone.status as ProjectMilestoneStatus)) {
    return false;
  }
  const dueMs = parseYyyyMmDdUtc(milestone.dueDate);
  if (!Number.isFinite(dueMs)) {
    return false;
  }
  const todayMs = parseYyyyMmDdUtc(toYyyyMmDdUtc(today));
  return dueMs < todayMs;
}

/**
 * True when last activity is older than `days` full calendar days before `now` (UTC).
 * Missing `lastActivityAt` → false (no alert until API provides it).
 */
export function isProjectInactiveDays(
  lastActivityAt: string | undefined,
  now: Date,
  days: number,
): boolean {
  if (!lastActivityAt?.trim()) {
    return false;
  }
  const last = new Date(lastActivityAt);
  if (!Number.isFinite(last.getTime())) {
    return false;
  }
  const thresholdMs = now.getTime() - days * 24 * 60 * 60 * 1000;
  return last.getTime() < thresholdMs;
}
